import { useEffect, useRef, type KeyboardEvent } from 'react';
import { DURATION, EASE } from '@constants/motion';
import { prefersReducedMotion } from '@lib/utils/motion';
import { num, numCompact } from '@lib/format';
import { useSlidingIndicator } from '@lib/hooks';

/**
 * `label` is what shows (keep it short); `name`, when set, is the full accessible name (include the count). `badge`
 * shows only when not zero (something to attend to); `count` always shows, muted at zero (how many there are);
 * `countMore`: the count is a lower bound ("1.000+").
 */
export interface TabItem<T extends string> { id: T; label: string; name?: string; badge?: number; badgeTone?: 'critical' | 'amber' | 'neutral'; count?: number | null; countMore?: boolean }

/** ids that tie a tab to its panel: spread `tabPanelProps(prefix, id)` on the panel element. */
const tabId = (prefix: string, id: string) => `${prefix}-tab-${id}`;
const panelId = (prefix: string, id: string) => `${prefix}-panel-${id}`;
export const tabPanelProps = (prefix: string, id: string) => ({ id: panelId(prefix, id), role: 'tabpanel', 'aria-labelledby': tabId(prefix, id), tabIndex: 0 }) as const;

/**
 * WAI-ARIA tabs: arrow keys, Home and End move between tabs (automatic activation); the underline slides to the selected
 * tab and the new panel steps in.
 */
export function Tabs<T extends string>({ label, items, value, onChange, idPrefix }: { label: string; items: TabItem<T>[]; value: T; onChange: (id: T) => void; idPrefix: string }) {
  const list = useRef<HTMLDivElement>(null);
  const bar = useSlidingIndicator(list, "[role='tab'][aria-selected='true']", value);
  // another tab: its panel steps in (the same stepin as an operations step), never on the first paint
  const shown = useRef(value);
  useEffect(() => {
    if (shown.current === value) return;
    shown.current = value;
    const panel = document.getElementById(panelId(idPrefix, value));
    if (!panel || prefersReducedMotion() || typeof panel.animate !== 'function') return;
    panel.animate([{ opacity: 0, transform: 'translateX(12px)' }, { opacity: 1, transform: 'none' }], { duration: DURATION.base, easing: EASE.out });
  }, [value, idPrefix]);
  function onKey(e: KeyboardEvent<HTMLDivElement>) {
    const i = items.findIndex((t) => t.id === value);
    const next = { ArrowRight: i + 1, ArrowLeft: i - 1, Home: 0, End: items.length - 1 }[e.key];
    if (next === undefined) return;
    e.preventDefault();
    const t = items[(next + items.length) % items.length];
    onChange(t.id);
    document.getElementById(tabId(idPrefix, t.id))?.focus();
  }
  return (
    <div ref={list} className="tabs" role="tablist" aria-label={label} onKeyDown={onKey}>
      {items.map((t) => (
        <button
          key={t.id}
          id={tabId(idPrefix, t.id)}
          type="button"
          role="tab"
          aria-selected={t.id === value}
          aria-controls={panelId(idPrefix, t.id)}
          aria-label={t.name}
          title={t.name}
          tabIndex={t.id === value ? 0 : -1}
          onClick={() => onChange(t.id)}
        >
          {t.label}
          {!!t.badge && ' '}
          {!!t.badge && <span className={`tab-badge ${t.badgeTone ?? 'neutral'}`} title={num(t.badge)}>{numCompact(t.badge)}</span>}
          {t.count != null && ' '}
          {t.count != null && <span className={`tab-count ${t.count === 0 ? 'is-zero' : ''}`}>{num(t.count)}{t.countMore && '+'}</span>}
        </button>
      ))}
      <span ref={bar} className="tabs-bar" aria-hidden="true" />
    </div>
  );
}
